// src/components/ServiceSettingsPanel.js
//
// SECTION 539: the General Chairperson says how claims are served on each property his committee holds; the Penalty
// Time Claim's receipt line follows (src\data\serviceSettings.js). Written to serviceSettings/{committee}; the rules
// let only that committee's GC (or the administrator) write it.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { AGREEMENT_LOOKUP } from '../data/agreementData';
import { RECEIPTS, RECEIPT_KEYS, committeeAgreements, agreementSlug, settingPatch, serviceFor } from '../data/serviceSettings';
import { getClaimRequirements } from '../data/claimRequirements';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function ServiceSettingsPanel({ user, committee }) {
  const [cur, setCur] = useState(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [msg, setMsg] = useState('');
  // Forensic audit 2026-10-16: a failed read used to become an empty record ({}), the panel showed every property on
  // "the agreement default", and Save then rewrote the whole `agreements` map from that empty record - erasing every
  // other property's setting. A failed read is now its own state: the error is shown and nothing can be saved.
  const [readErr, setReadErr] = useState('');
  useEffect(() => {
    if (!committee) return;
    setReadErr('');
    getDoc(doc(db, 'serviceSettings', committee)).then(d => setCur(d.exists() ? d.data() : {}))
      .catch((e) => { setCur(null); setReadErr('Could not read your service settings: ' + ((e && (e.code || e.message)) || 'refused') + '. What is on file is unknown, so nothing can be saved here until it can be read (reload the page).'); });
  }, [committee]);
  if (!committee) return null;
  const names = committeeAgreements(AGREEMENT_LOOKUP, committee);
  const save = async (name) => {
    if (readErr || cur === null) return;   // Forensic audit 2026-10-16: never save against a record nobody has read
    const d = draft[name] || {};
    const slug = agreementSlug(name);
    const set = (cur && cur.agreements && cur.agreements[slug]) || {};
    const r = settingPatch(cur, name, d.receipt || set.receipt, d.note !== undefined ? d.note : set.note);
    if (!r.ok) { setMsg(r.errors.join(' ')); return; }
    try {
      // Forensic audit 2026-10-16: THIS property's key only. setDoc with merge deep-merges the `agreements` map, so the
      // other properties' entries stand as the server holds them, whatever this screen last read. firestore.rules
      // (serviceSettings/{committee}) tests the merged document - committee, agreements a map, updatedBy, updatedAt - so
      // the narrower write passes the same rule.
      await setDoc(doc(db, 'serviceSettings', committee),
        { committee, agreements: { [slug]: r.value.agreements[slug] }, updatedBy: user.uid, updatedAt: serverTimestamp() },
        { merge: true });
      setCur((c) => ({ ...(c || {}), agreements: { ...((c && c.agreements) || {}), [slug]: r.value.agreements[slug] } })); setMsg('Saved for ' + name + '.');
    } catch (e) { setMsg('Not saved: ' + ((e && (e.code || e.message)) || 'refused')); }
  };
  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>How claims are served on your properties</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>The receipt line at the foot of every Penalty Time Claim follows what you set here, property by property.</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Set'}</button>
      </div>
      {open ? (
        <div style={{ padding: '0 14px 12px' }}>
          {readErr ? <div style={{ fontSize: 12, color: C.bad }}>&#9888; {readErr}</div>
            : cur === null ? <div style={{ fontSize: 12, color: C.dim }}>Reading…</div> : names.map(name => {
            const set = (cur.agreements && cur.agreements[agreementSlug(name)]) || null;
            const eff = serviceFor(name, getClaimRequirements(name), cur);
            const d = draft[name] || {};
            return (
              <div key={name} style={{ borderTop: '1px solid ' + C.line, padding: '8px 0', fontSize: 12, color: C.text }}>
                <div style={{ fontWeight: 700 }}>{name}</div>
                <div style={{ color: C.dim, margin: '2px 0 6px' }}>Now: {set ? RECEIPTS[set.receipt].label + (set.note ? ' - ' + set.note : '') : (eff ? 'the agreement default (' + (eff.receipt ? eff.receipt.left : 'no receipt line') + ')' : 'hand delivery (the default)')}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <select aria-label="How this committee receives service" className="form-input" style={{ width: 'auto', fontSize: 12 }} value={d.receipt || (set && set.receipt) || ''} onChange={e => setDraft(x => ({ ...x, [name]: { ...d, receipt: e.target.value } }))}>
                    <option value="">Choose…</option>
                    {RECEIPT_KEYS.map(k => <option key={k} value={k}>{RECEIPTS[k].label}</option>)}
                  </select>
                  <input aria-label="Note printed on the claim (optional), e.g. the understanding it rests on" className="form-input" spellCheck placeholder="Note printed on the claim (optional), e.g. the understanding it rests on" value={d.note !== undefined ? d.note : ((set && set.note) || '')} onChange={e => setDraft(x => ({ ...x, [name]: { ...d, note: e.target.value } }))} style={{ flex: 1, minWidth: 220, fontSize: 12 }} />
                  <button className="btn btn-primary btn-sm" disabled={!!readErr} onClick={() => save(name)}>Save</button>
                </div>
              </div>
            );
          })}
          {msg ? <div style={{ fontSize: 12, color: /^Not saved/.test(msg) ? C.bad : C.ok, marginTop: 6 }}>{msg}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
